/**
 * VideoDisplay — full-screen broadcast-style review wall for TVs / spare iPads.
 *
 * Shows a grid of events (vault/bars/beam/floor by default); each tile loops the
 * most recent clip for that event with the athlete's name overlaid. New clips push
 * in live via socket.io the moment they finish uploading (no wait for analysis).
 * Click/tap a tile to focus that event with Replay / Previous / Next controls.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext.js';
import api from '../api/index.js';
import { GYMNASTICS_EVENTS } from '../utils/constants.js';

const DEFAULT_EVENTS = ['vault', 'uneven_bars', 'balance_beam', 'floor_exercise'];
const MAX_HISTORY = 25;

const eventLabel = (value) =>
  GYMNASTICS_EVENTS.find(e => e.value === value)?.label || value;

function apiBase() {
  return window.location.hostname !== 'localhost'
    ? `${window.location.protocol}//${window.location.hostname}`
    : 'http://localhost:5001';
}

const resolveUrl = (u) => (!u ? '' : (u.startsWith('http') ? u : `${apiBase()}${u}`));

export default function VideoDisplay() {
  const { user } = useAuth();
  const [selectedEvents, setSelectedEvents] = useState(DEFAULT_EVENTS);
  const [clipsByEvent, setClipsByEvent] = useState({}); // { event: [clip, …] (newest first) }
  const [focusedEvent, setFocusedEvent] = useState(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const [showSettings, setShowSettings] = useState(false);

  const addClip = useCallback((clip) => {
    if (!clip?.event) return;
    if (user?.teamId && clip.teamId && clip.teamId !== user.teamId) return;
    setClipsByEvent(prev => {
      const existing = prev[clip.event] || [];
      if (existing.some(c => c.videoId === clip.videoId)) return prev;
      return { ...prev, [clip.event]: [clip, ...existing].slice(0, MAX_HISTORY) };
    });
  }, [user?.teamId]);

  // Seed from recent clips on mount + whenever the event selection changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `${apiBase()}/api/videos/recent?limit=60&events=${selectedEvents.join(',')}`
        );
        if (cancelled) return;
        const grouped = {};
        for (const clip of data) {
          (grouped[clip.event] = grouped[clip.event] || []).push(clip);
        }
        setClipsByEvent(grouped); // already newest-first from the API
      } catch {
        /* leave whatever we have */
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEvents]);

  // Live updates.
  useEffect(() => {
    const s = io(apiBase());
    s.on('video:uploaded', addClip);
    return () => s.disconnect();
  }, [addClip]);

  const cols = useMemo(() => {
    const n = selectedEvents.length;
    if (n <= 1) return 'grid-cols-1';
    if (n <= 4) return 'grid-cols-2';
    return 'grid-cols-3';
  }, [selectedEvents.length]);

  const toggleEvent = (value) =>
    setSelectedEvents(evts =>
      evts.includes(value) ? evts.filter(e => e !== value) : [...evts, value]
    );

  return (
    <div className="fixed inset-0 z-50 bg-black text-white flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-black/80">
        <div className="text-sm font-semibold tracking-wide text-gray-300">
          📺 Review Wall {user?.teamName ? `· ${user.teamName}` : ''}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(s => !s)}
            className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20"
          >
            Events
          </button>
          <button
            onClick={() => {
              if (document.fullscreenElement) document.exitFullscreen?.();
              else document.documentElement.requestFullscreen?.();
            }}
            className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20"
          >
            Fullscreen
          </button>
        </div>
      </div>

      {/* Event picker */}
      {showSettings && (
        <div className="px-4 py-3 bg-gray-900 flex flex-wrap gap-2">
          {GYMNASTICS_EVENTS.map(e => (
            <button
              key={e.value}
              onClick={() => toggleEvent(e.value)}
              className={[
                'text-xs px-3 py-1.5 rounded-full border',
                selectedEvents.includes(e.value)
                  ? 'bg-blue-600 border-blue-500 text-white'
                  : 'bg-transparent border-gray-600 text-gray-300',
              ].join(' ')}
            >
              {e.label}
            </button>
          ))}
        </div>
      )}

      {/* Grid or focus view */}
      {focusedEvent ? (
        <FocusView
          event={focusedEvent}
          clips={clipsByEvent[focusedEvent] || []}
          index={focusIndex}
          setIndex={setFocusIndex}
          onBack={() => setFocusedEvent(null)}
        />
      ) : (
        <div className={`flex-1 grid ${cols} gap-1 p-1 overflow-hidden`}>
          {selectedEvents.length === 0 ? (
            <div className="flex items-center justify-center text-gray-500">
              No events selected — tap “Events” to choose.
            </div>
          ) : (
            selectedEvents.map(evt => (
              <EventTile
                key={evt}
                event={evt}
                clip={(clipsByEvent[evt] || [])[0] || null}
                onClick={() => { setFocusedEvent(evt); setFocusIndex(0); }}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function EventTile({ event, clip, onClick }) {
  return (
    <div
      onClick={onClick}
      className="relative bg-gray-950 rounded overflow-hidden cursor-pointer group"
    >
      {/* Event label */}
      <div className="absolute top-2 left-2 z-10 text-xs font-bold uppercase tracking-wider bg-black/60 px-2 py-1 rounded">
        {eventLabel(event)}
      </div>

      {clip ? (
        <>
          <video
            key={clip.videoId}
            src={resolveUrl(clip.url)}
            className="w-full h-full object-contain bg-black"
            autoPlay
            loop
            muted
            playsInline
          />
          {/* Athlete name */}
          <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/85 to-transparent px-3 pt-8 pb-3">
            <div className="text-lg md:text-2xl font-extrabold leading-tight">{clip.athleteName}</div>
            <div className="text-[11px] text-gray-300">{new Date(clip.createdAt).toLocaleTimeString()}</div>
          </div>
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-600 text-sm">
          Waiting for {eventLabel(event)}…
        </div>
      )}
    </div>
  );
}

function FocusView({ event, clips, index, setIndex, onBack }) {
  const videoRef = useRef(null);
  const clip = clips[index] || null;

  const replay = () => {
    const v = videoRef.current;
    if (v) { v.currentTime = 0; v.play?.(); }
  };

  // index 0 = newest; "Previous" goes older (higher index), "Next" goes newer.
  const older = () => setIndex(i => Math.min(i + 1, clips.length - 1));
  const newer = () => setIndex(i => Math.max(i - 1, 0));

  return (
    <div className="flex-1 flex flex-col bg-black">
      <div className="flex items-center justify-between px-4 py-2">
        <div className="text-sm font-bold uppercase tracking-wider text-gray-300">
          {eventLabel(event)}
          {clips.length > 0 && (
            <span className="ml-2 text-gray-500 font-normal">
              {index === 0 ? 'Latest' : `${index} back`} · {clips.length} clip{clips.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <button onClick={onBack} className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20">
          ✕ Back to wall
        </button>
      </div>

      <div className="flex-1 relative flex items-center justify-center">
        {clip ? (
          <>
            <video
              ref={videoRef}
              key={clip.videoId}
              src={resolveUrl(clip.url)}
              className="max-h-full max-w-full object-contain"
              autoPlay
              loop
              muted
              playsInline
            />
            <div className="absolute bottom-4 left-4 z-10 bg-black/60 px-4 py-2 rounded">
              <div className="text-2xl md:text-4xl font-extrabold">{clip.athleteName}</div>
              <div className="text-xs text-gray-300">{new Date(clip.createdAt).toLocaleTimeString()}</div>
            </div>
          </>
        ) : (
          <div className="text-gray-600">No clips yet for {eventLabel(event)}.</div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-3 py-3 bg-black/80">
        <button
          onClick={older}
          disabled={index >= clips.length - 1}
          className="px-4 py-2 rounded bg-white/10 hover:bg-white/20 disabled:opacity-30"
        >
          ◀ Previous
        </button>
        <button onClick={replay} className="px-6 py-2 rounded bg-blue-600 hover:bg-blue-500 font-semibold">
          ↻ Replay
        </button>
        <button
          onClick={newer}
          disabled={index <= 0}
          className="px-4 py-2 rounded bg-white/10 hover:bg-white/20 disabled:opacity-30"
        >
          Next ▶
        </button>
      </div>
    </div>
  );
}
